
import { useState } from "react";
import { ShareIcon, Users, Trophy } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useReferrals } from "@/hooks/useReferrals";
import ReferralStats from "./referral/ReferralStats";
import ReferralLinkCopy from "./referral/ReferralLinkCopy";
import ReferredFriendsList from "./referral/ReferredFriendsList";
import RewardsList from "./referral/RewardsList";
import ReferralMilestones from "./referral/ReferralMilestones";
import ReferralSectionLoading from "./referral/ReferralSectionLoading";

const ReferralSection = () => {
  const { 
    referralLink, 
    referredFriends, 
    referralStats, 
    rewards,
    isLoading,
    copyReferralLink,
    claimReward 
  } = useReferrals();
  
  const [claimingRewards, setClaimingRewards] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState("link");

  const handleClaimReward = async (rewardId: string) => {
    setClaimingRewards(prev => ({ ...prev, [rewardId]: true }));
    try {
      await claimReward(rewardId);
    } finally {
      setClaimingRewards(prev => ({ ...prev, [rewardId]: false }));
    }
  };

  if (isLoading) {
    return <ReferralSectionLoading />;
  }

  return (
    <Card className="shadow border-0 overflow-hidden">
      <CardHeader className="pb-2 px-6 pt-6 bg-gradient-to-r from-[#F3F7FC] to-white">
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl font-semibold text-[#4A607B] flex items-center">
            <ShareIcon className="mr-2 h-5 w-5" />
            Referral Program
          </CardTitle>
          
          <ReferralStats 
            totalReferrals={referralStats.totalReferrals}
            paidReferrals={referralStats.paidReferrals}
            nextMilestone={referralStats.nextMilestone}
          />
        </div>
      </CardHeader>
      
      <CardContent className="px-6 pb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="grid grid-cols-3 w-full max-w-md mb-4">
            <TabsTrigger value="link" className="text-sm">
              <ShareIcon className="w-4 h-4 mr-1.5" /> 
              Share
            </TabsTrigger>
            <TabsTrigger value="friends" className="text-sm">
              <Users className="w-4 h-4 mr-1.5" /> 
              Friends
            </TabsTrigger>
            <TabsTrigger value="rewards" className="text-sm">
              <Trophy className="w-4 h-4 mr-1.5" /> 
              Rewards
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="link" className="mt-0">
            <div className="space-y-4">
              <ReferralLinkCopy 
                referralLink={referralLink}
                copyReferralLink={copyReferralLink}
              />
              <ReferralMilestones />
            </div>
          </TabsContent>
          
          <TabsContent value="friends" className="mt-0">
            <ReferredFriendsList referredFriends={referredFriends} />
          </TabsContent>
          
          <TabsContent value="rewards" className="mt-0">
            <RewardsList 
              rewards={rewards}
              claimingRewards={claimingRewards}
              onClaimReward={handleClaimReward}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ReferralSection;
